import { useCallback, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    generatePath,
    matchPath,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom';
import { CaretLeftIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { SidePanelSlotsContext } from '@/components/SidePanel';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    hasRouterHistory,
    useNavigationHistoryStore,
} from '@/stores/navigation-history.store';
import {
    getBackToLabel,
    getParentRoute,
    matchRoutePattern,
} from '@/lib/route-config';
import { appendSearchParams } from '@/utils/preserve-params';

type BackButtonVariant = 'static' | 'floating';

interface LocationState {
    readonly returnUrl?: string;
}

export interface BackButtonProps {
    readonly fallbackPath?: string;
    readonly label?: string;
    readonly className?: string;
    readonly showLabel?: boolean;
    readonly onBack?: () => void;
    readonly variant?: BackButtonVariant;
}

const VARIANT_STYLES: Record<BackButtonVariant, string> = {
    static: '',
    floating:
        'absolute top-6 left-0 z-40 max-w-full bg-grey-600/80 backdrop-blur-sm hover:bg-grey-600',
};

function extractPathname(url: string): string {
    return url.split('?')[0] ?? url;
}

export function BackButton({
    fallbackPath = '/',
    label: overrideLabel,
    className,
    showLabel = true,
    onBack,
    variant = 'static',
}: BackButtonProps) {
    const leftPanelSlot = useContext(SidePanelSlotsContext).left;
    const { t } = useTranslation(I18nNamespace.COMMON);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const hasHistory = hasRouterHistory();
    const previousEntry = useNavigationHistoryStore((state) =>
        state.currentIndex > 0 ? state.history[state.currentIndex - 1] : null,
    );

    const returnUrl = (location.state as LocationState | null)?.returnUrl;
    const currentRoute = matchRoutePattern(location.pathname);
    const parentRoute = currentRoute ? getParentRoute(currentRoute) : null;

    const resolvedParentPath = useMemo(() => {
        if (!parentRoute || !currentRoute) return null;

        const match = matchPath(currentRoute, location.pathname);
        if (!match?.params || Object.keys(match.params).length === 0) {
            return parentRoute;
        }

        try {
            const generatedPath = generatePath(
                parentRoute as string,
                match.params as Record<string, string>,
            );
            return generatedPath.includes('/:') ? null : generatedPath;
        } catch {
            return null;
        }
    }, [parentRoute, currentRoute, location.pathname]);

    const label = useMemo(() => {
        if (overrideLabel) return overrideLabel;

        if (hasHistory && previousEntry?.route) {
            return t(getBackToLabel(previousEntry.route));
        }

        if (returnUrl) {
            const route = matchRoutePattern(extractPathname(returnUrl));
            if (route) return t(getBackToLabel(route));
        }

        if (parentRoute) return t(getBackToLabel(parentRoute));

        return t('button.back');
    }, [
        overrideLabel,
        hasHistory,
        previousEntry?.route,
        returnUrl,
        parentRoute,
        t,
    ]);

    const withParams = useCallback(
        (path: string) => appendSearchParams(path, searchParams.toString()),
        [searchParams],
    );

    const handleBack = useCallback(() => {
        if (onBack) {
            onBack();
            return;
        }

        if (hasHistory) {
            navigate(-1);
            return;
        }

        if (returnUrl) {
            navigate(withParams(returnUrl));
            return;
        }

        navigate(withParams(resolvedParentPath ?? fallbackPath));
    }, [
        navigate,
        onBack,
        hasHistory,
        returnUrl,
        resolvedParentPath,
        fallbackPath,
        withParams,
    ]);

    const button = (
        <Button
            variant="ghost"
            onClick={handleBack}
            className={cn(VARIANT_STYLES[variant], className)}
            leadingIcon={<CaretLeftIcon className="w-4 h-4" />}
            aria-label={t('ariaLabels.goBack')}
        >
            {showLabel && <span className="truncate">{label}</span>}
        </Button>
    );

    if (variant === 'floating' && leftPanelSlot) {
        return createPortal(button, leftPanelSlot);
    }

    return button;
}
